import { Component, HostBinding, Input, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInputModule, MatLabel } from '@angular/material/input';
import { isEmptyObject, getIcons } from '@nagel-utils';
import { IconComponent } from '@nagel-assests';
import { BaseChangeEvent, CheckboxGroupType, FieldOption, IconSize } from '@nagel-types';
import { BaseFieldComponent } from '../base-field/base-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';

export type CheckboxGroup = {
    [key: string]: string;
};

@Component({
    selector: 'lib-checkboxes-group',
    imports: [
        IconComponent,
        ReactiveFormsModule,
        MatCheckbox,
        MatLabel,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatDividerModule
    ],
    templateUrl: './checkboxes-group.component.html',
    styleUrl: './checkboxes-group.component.scss'
})
export class CheckboxesGroupComponent extends BaseFieldComponent implements ControlValueAccessor, OnDestroy, OnInit, OnChanges {
@Input() fieldKey?: string = '';
@Input() className?: string = '';
@Input() labelClassName?: string = '';
@Input() options?: FieldOption[];
@Input() includeDividers?: boolean = false;
@Input() defaultValues?: CheckboxGroupType = {};
@Input() areCheckboxesDisabled = false;
@Input() onChange?: (event: BaseChangeEvent) => void;
checkboxGroupForm!: FormGroup;
stateChanges = new Subject<void>();
focused = false;
required = false;
private _placeholder = '';
static nextId = 0;

  @HostBinding() id = `mat-group-${CheckboxesGroupComponent.nextId++}`;

  getFormControlDefaultValue = (option: FieldOption) => {
      const defaultValue = this.defaultValues?.[option.value as string];

      return defaultValue || false;
  };

  createCheckboxGroup = (options: FieldOption[] = []) => {
      return options.reduce((acc, option) => {

          return {
              ...acc,
              [option.value as string]: new FormControl(this.getFormControlDefaultValue(option))
          };
      }, {} as { [key: string]: FormControl });
  };

  ngOnInit() {
      this.checkboxGroupForm = new FormGroup(this.createCheckboxGroup(this.options));

      this.registerOnChange(this.handleValueUpdate);

      if (this.areCheckboxesDisabled) {
          this.checkboxGroupForm.disable();
      }
  }

  ngOnChanges(changes: SimpleChanges) {
      if (changes['defaultValues'] && this.checkboxGroupForm) {
          this.checkboxGroupForm.patchValue(this.defaultValues || {}, { emitEvent: false });
      }
  }

  getIcons = (icon: string | string[]) : string[] => getIcons(icon);

  @Input()
  set value(value: CheckboxGroupType) {
      if (this.checkboxGroupForm) {
          this.checkboxGroupForm.patchValue(value);
          this.stateChanges.next();
      }
  }

  get value() {
      return this.checkboxGroupForm.value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
      return this.focused || !this.empty;
  }

  get empty() {
      const formValue = this.checkboxGroupForm.value;
      return !Object.values(formValue).some(value => value);
  }

  get placeholder(): string {
      return this._placeholder;
  }

  set placeholder(value: string) {
      this._placeholder = value;
      this.stateChanges.next();
  }

  get errorState(): boolean {
      return this.checkboxGroupForm.invalid;
  }

  onTouched = () => {};

  onChangeSubs: Subscription[] = [];

  ngOnDestroy() {
      this.stateChanges.complete();
      this.onChangeSubs.forEach(sub => sub.unsubscribe());
  }

  writeValue(value: CheckboxGroupType): void {
      if (value && !isEmptyObject(value) && !isEmptyObject(this.checkboxGroupForm.controls)) {
          this.checkboxGroupForm.setValue(value, { emitEvent: false });
      }
      this.stateChanges.next();
  }

  registerOnChange(onChange: (value: CheckboxGroupType) => void): void {
      if (this.checkboxGroupForm) {
          const subscription = this.checkboxGroupForm.valueChanges.subscribe(onChange);
          this.onChangeSubs.push(subscription);
      }
  }

  registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
  }

  getLabelPosition = (option: FieldOption) => {
      let labelPlacement = 'after';
      const hasIcon = option.icon !== '';
      const isBeforePosition = option.iconOptions?.position === 'before';

      if (hasIcon) {
          labelPlacement = 'before';
      }

      if (isBeforePosition) {
          labelPlacement = 'after';
      }

      return labelPlacement;
  };

  getIconSize = (option: FieldOption) => {
      return option.iconOptions?.size || '24x24' as IconSize;
  };

  private handleValueUpdate = (value: CheckboxGroupType) => {
      if (value) {
          if (this.onChange) {
              this.onChange({ value, key: this.fieldKey });
          }
      }
  };

  getIsChecked = (option: FieldOption) => {
      return this.checkboxGroupForm.value[option.value as string];
  };

  getCheckboxValue = (option: FieldOption) => {
      return option.value as string || '';
  };

  getIcon = (option: FieldOption) => {
      return option.icon as string || '';
  };
}